import posthog from "posthog-js";

/**
 * PostHog dual-write (pilot). Fully no-op unless both env vars are set,
 * so production stays untouched until we explicitly enable it there.
 */
export const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
export const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST;

const POSTHOG_ENVIRONMENT = process.env.NEXT_PUBLIC_POSTHOG_ENV || process.env.NODE_ENV || "unknown";

let initialized = false;

export const isPostHogEnabled = (): boolean => typeof window !== "undefined" && Boolean(POSTHOG_KEY && POSTHOG_HOST);

/** True once posthog.init has run (used by the flags provider to await readiness). */
export const isPostHogInitialized = (): boolean => initialized;

/**
 * Calls made before initPostHog are queued and replayed on init. Without this,
 * everything fired before Analytics.init() has run (initial UTM props, the
 * first onboarding events, an early identify) raced init and was silently
 * dropped. Bounded so a failed init can't grow the queue forever.
 */
const preInitQueue: Array<() => void> = [];
const MAX_QUEUED_CALLS = 200;

const runOrQueue = (fn: () => void) => {
    if (initialized) {
        fn();
        return;
    }
    if (!isPostHogEnabled()) return;
    if (preInitQueue.length < MAX_QUEUED_CALLS) {
        preInitQueue.push(fn);
    }
};

/**
 * PostHog's $feature_flag_called is the $exposure analog, but the SDK sends it
 * directly, bypassing our wrappers. AnalyticsService registers a mirror here
 * (see the before_send tap in initPostHog) to copy each one into the backend
 * events table under the historical $exposure row shape.
 */
type ExposureMirror = (flagKey: string, rawValue: unknown) => void;
let exposureMirror: ExposureMirror | null = null;
export const setPostHogExposureMirror = (fn: ExposureMirror) => {
    exposureMirror = fn;
};

/**
 * posthog-js persists its identity (including an identified student_N) under
 * this key — in localStorage and, for cross-subdomain continuity, in a cookie.
 */
const hasPersistedIdentity = (): boolean => {
    const key = `ph_${POSTHOG_KEY}_posthog`;
    try {
        if (window.localStorage.getItem(key)) return true;
    } catch {}
    try {
        return document.cookie.split(";").some((c) => c.trim().startsWith(`${key}=`));
    } catch {
        return false;
    }
};

/** The distinct_id inside the SDK's persisted state (localStorage, then cookie). */
const readPersistedDistinctId = (): string | undefined => {
    const key = `ph_${POSTHOG_KEY}_posthog`;
    const parse = (raw: string | null | undefined): string | undefined => {
        if (!raw) return undefined;
        try {
            return JSON.parse(raw)?.distinct_id ?? undefined;
        } catch {
            return undefined;
        }
    };
    try {
        const fromStorage = parse(window.localStorage.getItem(key));
        if (fromStorage) return fromStorage;
    } catch {}
    try {
        const cookie = document.cookie.split(";").find((c) => c.trim().startsWith(`${key}=`));
        return parse(cookie && decodeURIComponent(cookie.split("=").slice(1).join("=")));
    } catch {
        return undefined;
    }
};

const clearPersistedIdentity = () => {
    const key = `ph_${POSTHOG_KEY}_posthog`;
    try {
        window.localStorage.removeItem(key);
    } catch {}
    try {
        // The cookie may be host-scoped or (posthog-js default) on the eTLD+1
        document.cookie = `${key}=; max-age=0; path=/`;
        const root = window.location.hostname.split(".").slice(-2).join(".");
        document.cookie = `${key}=; max-age=0; path=/; domain=.${root}`;
    } catch {}
};

/**
 * @param distinctId - the app's anonymous device id (localStorage user_uuid,
 * also the backend events-table device_id), so both share one anonymous
 * identity; identify() merges it into the person on login.
 */
export const initPostHog = (distinctId?: string) => {
    if (!isPostHogEnabled() || initialized) return;
    try {
        // An explicit ?dev_id in the URL is a deliberate identity handoff
        // (funnels -> app, QA links): the backend device id adopts the URL id
        // unconditionally, so when it differs from what the SDK persisted,
        // PostHog and the events table would continue DIFFERENT users. Dropping the persisted
        // state re-enables the bootstrap below with the URL id; on login,
        // identify(student_N) merges the ids back into one person.
        try {
            const urlDevId = new URLSearchParams(window.location.search).get("dev_id");
            if (urlDevId) {
                // The URL id is the head of the resolve chain regardless of caller
                distinctId = urlDevId;
                if (hasPersistedIdentity() && readPersistedDistinctId() !== urlDevId) {
                    clearPersistedIdentity();
                }
            }
        } catch {}

        // bootstrap.distinctID OVERRIDES whatever identity the SDK persisted,
        // so it is passed only on the very first visit (no persisted state) to
        // align the anonymous id with the backend device id. Re-bootstrapping
        // on every load used to reset an identified student_N back to the
        // device id after each reload.
        const bootstrap = distinctId && !hasPersistedIdentity() ? { bootstrap: { distinctID: distinctId } } : {};

        posthog.init(POSTHOG_KEY!, {
            api_host: POSTHOG_HOST,
            // Custom event stream is explicit trackEvent calls only (autocapture off).
            // Built-in $pageview/$pageleave are PostHog-only extras: the Web
            // Analytics module (Visitors/Views/Sessions) is computed from them
            // alone. "history_change" covers the initial load AND SPA routes.
            autocapture: false,
            capture_pageview: "history_change",
            capture_pageleave: true,
            // Recording starts only if the project has "Record user sessions"
            // enabled (server-driven via /flags), so this stays inert otherwise
            disable_session_recording: false,
            // Read-only tap for the exposure mirror; never alters events
            before_send: (payload: any) => {
                try {
                    const props = payload?.properties as Record<string, any> | undefined;
                    if (payload?.event === "$feature_flag_called" && exposureMirror && props?.["$feature_flag"]) {
                        exposureMirror(String(props["$feature_flag"]), props["$feature_flag_response"]);
                    }
                } catch {}
                return payload;
            },
            ...bootstrap
        });
        // `environment` lets us separate dev/prod data inside the single OSS project
        posthog.register({ environment: POSTHOG_ENVIRONMENT });
        initialized = true;

        const queued = preInitQueue.splice(0, preInitQueue.length);
        for (const fn of queued) {
            try {
                fn();
            } catch (e) {
                console.error("PostHog queued call error:", e);
            }
        }
    } catch (e) {
        console.error("PostHog init failed:", e);
    }
};

/**
 * Person properties are batched: every call is deduplicated against what this
 * pageload already sent and delivered as ONE standalone $set per burst
 * (FLUSH_AFTER_MS debounce) instead of one event per call — standalone $set
 * was ~35% of prod event volume, each one a Postgres person write. Business
 * events are deliberately NOT touched (no $set piggybacking on them) so their
 * payloads stay comparable with the historical (Amplitude-era) stream.
 */
let pendingPersonProps: Record<string, any> = {};
const sentPersonProps: Record<string, string> = {};
let flushTimer: ReturnType<typeof setTimeout> | undefined;
const FLUSH_AFTER_MS = 5_000;

const flushPendingPersonProps = () => {
    runOrQueue(() => {
        try {
            const pending = takePendingPersonProps();
            if (pending) posthog.setPersonProperties(pending);
        } catch (e) {
            console.error("PostHog person props flush error:", e);
        }
    });
};

const stashPersonProps = (props?: Record<string, any>) => {
    if (!isPostHogEnabled() || !props) return;
    for (const [key, value] of Object.entries(props)) {
        const fingerprint = JSON.stringify(value) ?? "undefined";
        if (sentPersonProps[key] === fingerprint) continue;
        pendingPersonProps[key] = value;
    }
    if (Object.keys(pendingPersonProps).length > 0 && !flushTimer) {
        flushTimer = setTimeout(flushPendingPersonProps, FLUSH_AFTER_MS);
    }
};

const takePendingPersonProps = (): Record<string, any> | undefined => {
    if (flushTimer) {
        clearTimeout(flushTimer);
        flushTimer = undefined;
    }
    if (Object.keys(pendingPersonProps).length === 0) return undefined;
    const taken = pendingPersonProps;
    pendingPersonProps = {};
    for (const [key, value] of Object.entries(taken)) {
        sentPersonProps[key] = JSON.stringify(value) ?? "undefined";
    }
    return taken;
};

export const posthogCapture = (eventName: string, props?: Record<string, any>) => {
    runOrQueue(() => {
        try {
            posthog.capture(eventName, props);
        } catch (e) {
            console.error("PostHog capture error:", e);
        }
    });
};

/** Unhandled errors reach PostHog error tracking, with the stack the console no longer has. */
export const posthogCaptureException = (error: unknown, props?: Record<string, any>) => {
    runOrQueue(() => {
        try {
            posthog.captureException(error, props);
        } catch (e) {
            console.error("PostHog captureException error:", e);
        }
    });
};

export const posthogIdentify = (distinctId: string, personProps?: Record<string, any>) => {
    runOrQueue(() => {
        try {
            stashPersonProps(personProps);
            // Re-identifying with the SAME id makes posthog-js emit a standalone
            // $set internally (our setUserId runs on every authed page load) —
            // skip it; the stashed props ride the next captured event instead.
            if (posthog.get_distinct_id() === distinctId) return;
            posthog.identify(distinctId, takePendingPersonProps());
        } catch (e) {
            console.error("PostHog identify error:", e);
        }
    });
};

export const posthogSetPersonProperties = (props: Record<string, any>) => {
    // No immediate posthog.setPersonProperties (= one $set event per call):
    // props are stashed, deduplicated and flushed in one batch, see stashPersonProps.
    stashPersonProps(props);
};

/**
 * Stop session replay for the rest of this page lifetime. Safe to call
 * repeatedly or when recording never started. Events keep flowing.
 */
export const posthogStopSessionRecording = () => {
    runOrQueue(() => {
        try {
            posthog.stopSessionRecording();
        } catch (e) {
            console.error("PostHog stop recording error:", e);
        }
    });
};

/**
 * Detach the current person (explicit logout): posthog.reset() rotates the
 * anonymous distinct_id. Returns the new id so the caller can re-align the
 * backend device id (localStorage user_uuid) and keep both on ONE anonymous identity.
 * No-op before init — there is nothing to detach yet.
 */
export const posthogReset = (): string | undefined => {
    if (!initialized) return undefined;
    try {
        posthog.reset();
        return posthog.get_distinct_id();
    } catch (e) {
        console.error("PostHog reset error:", e);
        return undefined;
    }
};

/** Link an additional anonymous id (e.g. the landing device id) to the current person. */
export const posthogAlias = (alias: string) => {
    runOrQueue(() => {
        try {
            posthog.alias(alias);
        } catch (e) {
            console.error("PostHog alias error:", e);
        }
    });
};
